import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  errorMessage,
  useCapacity,
  useCpuRuns,
  useCreateCpuRun,
  useEnvironments,
  useMyCpuWorkspaces,
} from "../api/queries";
import type {
  CpuCapacity,
  CpuRunState,
  EnvironmentOption,
} from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { ClusterProblem, isClusterProblem } from "../components/ClusterProblem";
import { CpuCapacitySection } from "../components/capacity/CpuCapacitySection";
import { WaitingSection } from "../components/capacity/WaitingSection";
import { CpuWorkspaceForm } from "../components/cpu/CpuWorkspaceForm";
import { PageHeader } from "../components/layout/PageHeader";
import { Field } from "../components/ui/Field";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/ui/StateBlock";
import { SUPPORT_CHANNEL } from "../config";
import { hasKeyFor } from "../lib/keyScopes";
import {
  commandStartHint,
  coresHint,
  coresStartNote,
  cpuRunStateLabel,
  cpuRunTone,
  fanOutSummary,
  largestTaskCores,
  launcherNote,
  MULTI_TASK_STORAGE_NOTE,
  RUN_PROMISES,
  suggestedRunName,
  summarizeCpuWorkspaceRow,
  summarizeRunRow,
  TASK_ID_NOTE,
  validateCpuRunForm,
} from "../lib/cpu";
import type { CpuRunFormErrors, CpuRunFormValues } from "../lib/cpu";
import { workspaceStateLabel, workspaceTone } from "../lib/workload";

interface RunFormProps {
  cpu: CpuCapacity;
  environments: EnvironmentOption[];
  /** The cluster-wide emergency stop, published by the server. */
  paused: boolean;
  /** The submitter, so the form can say where the command will run. */
  username: string;
}

/**
 * Two questions in front, and the third one folded: what to run, how wide —
 * and, on demand, how many times.
 *
 * Every bound on it comes from the server — the core ceiling is the number the
 * capacity payload published for the biggest machine the console can start,
 * the task ceiling is `MAX_TASKS_PER_RUN`, and the hours come from
 * `DEFAULT_RUN_HOURS` / `MAX_RUN_HOURS`. Nothing here is a constant compiled
 * into the bundle, because a deployment that changed one would otherwise be
 * contradicted by its own interface. The one bound with no published number is
 * `MAX_TASK_RETRIES`: the form asks for whole extra attempts and lets the
 * server's own refusal name the ceiling, rather than guessing it.
 *
 * Most runs are one command, once. `Tasks`, `Retries` and the throttle are the
 * fan-out, so they sit behind a closed disclosure rather than in front of
 * everybody who only wants the command run once — and nothing they hold is
 * ever hidden: the summary carries back anything that is no longer the
 * default, and a refusal from one of them opens the disclosure.
 */
function RunForm({
  cpu,
  environments,
  paused,
  username,
}: RunFormProps): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateCpuRun();
  const maxCores = largestTaskCores(cpu);
  const maxHours = cpu.defaults.max_run_hours;
  const maxTasks = cpu.defaults.max_tasks_per_run;

  const [values, setValues] = useState<CpuRunFormValues>({
    name: "",
    image: "",
    command: "",
    // `?? 1` would not do: a ceiling of 0 is not a ceiling, and the field
    // would open on a run nobody could submit.
    cores: maxCores !== null && maxCores > 0 ? maxCores : 1,
    hours: cpu.defaults.run_hours,
    // The defaults are the ones `docs/API.md` gives: one command, no silent
    // re-run, and as many at once as the fleet will give.
    tasks: 1,
    retries: 0,
    maxParallelTasks: null,
  });
  const [errors, setErrors] = useState<CpuRunFormErrors>({});
  /** Whether the researcher has taken the name over from the suggestion. */
  const [named, setNamed] = useState(false);
  const [fanOutOpen, setFanOutOpen] = useState(false);

  const image = values.image || environments[0]?.id || "";
  // A name nobody typed is still a name: the field shows what the command and
  // today's date make of it, and follows the command until somebody edits it.
  const suggestedName = suggestedRunName(values.command);
  const name = named ? values.name : suggestedName;
  // The one thing the form must say before anything is submitted: whether
  // these cores are free now or a machine has to start first.
  const startNote = coresStartNote(values.cores, cpu, values.tasks);
  const fanOut = fanOutSummary(values);

  const update = <K extends keyof CpuRunFormValues>(
    key: K,
    value: CpuRunFormValues[K],
  ): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const submit = (): void => {
    // An emptied field falls back to the suggestion rather than refusing: a
    // missing name is never the reason a run does not start.
    const candidate: CpuRunFormValues = {
      ...values,
      image,
      name: name.trim() || suggestedName,
    };
    const found = validateCpuRunForm(candidate, {
      maxCores,
      maxHours,
      maxTasks,
    });
    setErrors(found);
    if (found.tasks || found.retries || found.maxParallelTasks) {
      // A refusal is never left behind a closed door.
      setFanOutOpen(true);
    }
    if (Object.keys(found).length > 0) {
      return;
    }
    create.mutate(
      {
        name: candidate.name,
        image: candidate.image,
        command: candidate.command.trim(),
        cores: candidate.cores,
        hours: candidate.hours,
        tasks: candidate.tasks,
        retries: candidate.retries,
        max_parallel_tasks: candidate.maxParallelTasks,
      },
      { onSuccess: (run) => navigate(`/cpu/${run.id}`) },
    );
  };

  return (
    <form
      className="job-form"
      // The bounds are on the fields, for the spinners and for assistive
      // technology, but the browser's own refusal is not allowed to be the one
      // the researcher reads: "Value must be less than or equal to 62" names
      // no rule. Every refusal in this console says which rule it is and what
      // the number means, so the form validates itself.
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="job-form__row">
        <Field
          label="Name"
          htmlFor="run-name"
          error={errors.name}
          hint="Named after your command and today's date until you change it."
        >
          <input
            id="run-name"
            className="input"
            value={name}
            onChange={(event) => {
              setNamed(true);
              update("name", event.target.value);
            }}
          />
        </Field>

        <Field label="Environment" htmlFor="run-image" error={errors.image}>
          <select
            id="run-image"
            className="select"
            value={image}
            onChange={(event) => update("image", event.target.value)}
          >
            {environments.length === 0 ? (
              <option value="">No environment published</option>
            ) : null}
            {environments.map((option) => (
              <option key={option.id} value={option.id}>
                {option.added_by
                  ? `${option.name} — added by ${option.added_by}`
                  : option.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field
        label="Command"
        htmlFor="run-command"
        error={errors.command}
        // What a run is — no shell, no way in — is said once, above the form,
        // where the choice between the two kinds is made. This says the one
        // thing that belongs to the command itself: where it starts, which is
        // a different folder at one task and at many.
        hint={commandStartHint(username, values.tasks)}
      >
        <textarea
          id="run-command"
          className="textarea textarea--mono"
          value={values.command}
          spellCheck={false}
          onChange={(event) => update("command", event.target.value)}
        />
      </Field>

      <div className="job-form__row">
        <Field
          label="Cores per task"
          htmlFor="run-cores"
          error={errors.cores}
          hint={coresHint(cpu)}
        >
          <input
            id="run-cores"
            className="input input--narrow"
            type="number"
            min={1}
            {...(maxCores === null ? {} : { max: maxCores })}
            value={values.cores}
            onChange={(event) => update("cores", Number(event.target.value))}
          />
        </Field>
        <Field
          label="Hours"
          htmlFor="run-hours"
          error={errors.hours}
          hint={`1 to ${maxHours}. You can add more time later, in one click.`}
        >
          <input
            id="run-hours"
            className="input input--narrow"
            type="number"
            min={1}
            max={maxHours}
            value={values.hours}
            onChange={(event) => update("hours", Number(event.target.value))}
          />
        </Field>
      </div>

      <details
        className="job-form__more"
        open={fanOutOpen}
        onToggle={(event) => setFanOutOpen(event.currentTarget.open)}
      >
        <summary>
          More: run the command many times
          {fanOut ? (
            <span className="job-form__more-recap"> — {fanOut}</span>
          ) : null}
        </summary>
        <div className="job-form__more-body">
          {/* Said before the fields rather than after the refusal: the gate is
              the server's and it is not published, so the rule is stated and
              the server's own sentence is what arrives if it bites. */}
          <p className="form__note">{MULTI_TASK_STORAGE_NOTE}</p>

          <Field
            label="Tasks"
            htmlFor="run-tasks"
            error={errors.tasks}
            hint={`1 to ${maxTasks}. ${TASK_ID_NOTE}`}
          >
            <input
              id="run-tasks"
              className="input input--narrow"
              type="number"
              min={1}
              max={maxTasks}
              value={values.tasks}
              onChange={(event) => update("tasks", Number(event.target.value))}
            />
          </Field>

          <div className="job-form__row">
            <Field
              label="Retries"
              htmlFor="run-retries"
              error={errors.retries}
              // The rule, not the mechanism: the console will not run a command
              // twice unless the person who wrote it says that is safe.
              hint={
                "None, unless you say a failed task may be run again. A " +
                "command that may have written something is never silently " +
                "repeated — if your tasks are idempotent, one output per " +
                "$TASK_ID as sweeps usually are, ask for a retry and a task " +
                "whose machine went away is run again."
              }
            >
              <input
                id="run-retries"
                className="input input--narrow"
                type="number"
                min={0}
                value={values.retries}
                onChange={(event) =>
                  update("retries", Number(event.target.value))
                }
              />
            </Field>
            <Field
              label="At most this many at once"
              htmlFor="run-parallel"
              error={errors.maxParallelTasks}
              hint={
                "Empty means as many as the fleet will give you, which is the " +
                "usual answer. Set a number only to spare something your " +
                "tasks share — an API that rate-limits you, one database."
              }
            >
              <input
                id="run-parallel"
                className="input input--narrow"
                type="number"
                min={1}
                value={values.maxParallelTasks ?? ""}
                onChange={(event) =>
                  update(
                    "maxParallelTasks",
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  )
                }
              />
            </Field>
          </div>
        </div>
      </details>

      {startNote ? (
        // Needing a machine is not a refusal — the console starts one and the
        // run begins by itself — so this is a note, never an error.
        <p className="form__note" role="status">
          {startNote}
        </p>
      ) : null}

      {paused ? (
        <p className="form__error" role="status">
          The console is paused right now — no run can start. Work already
          running is untouched.
        </p>
      ) : null}

      {create.isError ? (
        // The server's refusals are written for this reader and name what to do
        // — the workload holding your files, the size of a machine, the fleet's
        // ceiling. They are shown exactly as they were written.
        <p className="form__error" role="alert">
          {errorMessage(create.error)}
        </p>
      ) : null}

      <button
        type="submit"
        className="btn btn--primary"
        disabled={
          create.isPending ||
          paused ||
          environments.length === 0 ||
          maxCores === null
        }
      >
        {create.isPending ? "Submitting…" : "Run it"}
      </button>
    </form>
  );
}

/**
 * What a run is: four lines, and the four paragraphs a click below them.
 *
 * The paragraphs are the promises and every one of them is still here — but a
 * block of four is a block nobody finishes, so the line carries what happens
 * to you and the paragraph carries why.
 */
function RunPromises(): JSX.Element {
  return (
    <section className="section" aria-labelledby="cpu-promises-title">
      <h2 className="section__title" id="cpu-promises-title">
        What a run is
      </h2>
      <ul className="promises">
        {RUN_PROMISES.map((promise) => (
          <li className="promises__item" key={promise.headline}>
            {promise.headline}
          </li>
        ))}
      </ul>
      <details className="promises__more">
        <summary>Read the full promises</summary>
        <div className="promises">
          {RUN_PROMISES.map((promise) => (
            <p className="promises__item" key={promise.headline}>
              {promise.full}
            </p>
          ))}
        </div>
      </details>
    </section>
  );
}

/** Which of the three things this fleet offers is being asked for. */
type CpuAction = "workspace" | "run" | "launcher";

/** One primary action: the pill that chooses it, and what it gets you. */
interface CpuActionChoice {
  action: CpuAction;
  label: string;
  note: string;
}

/**
 * The two primary actions, each with the one sentence that separates them.
 *
 * They are not variants of each other — one is a machine you live on, the
 * other is a command that runs to the end — so the sentence under the choice
 * says which is which before either form is read. The third card is built
 * beside these rather than listed here, because its sentence carries a number
 * only the server has.
 */
const CPU_ACTIONS: readonly CpuActionChoice[] = [
  {
    action: "workspace",
    label: "Cores and a shell",
    note:
      "A machine you live on: the cores you ask for, a shell over SSH and " +
      "your own files, until the deadline you set.",
  },
  {
    action: "run",
    label: "Run a command",
    note:
      "A command, run once or as many times as you say. There is no shell " +
      "and no way in: it runs to the end and you read the log.",
  },
];

/** The label of the third card, said the way `docs/PRODUCT.md` says it. */
const LAUNCHER_LABEL = "A terminal to drive runs from";

/** How many runs the list asks for, and the number its footnote names. */
const RUN_LIST_LIMIT = 50;

/**
 * The states the runs list may be narrowed to, and the word for each.
 *
 * One state per chip, because `state` is one state: the API takes a single
 * value and there is no "live" or "finished" on the wire, so a chip standing
 * for a group would be this bundle inventing a vocabulary the server does not
 * have. `null` is the absence of the parameter.
 */
const RUN_FILTERS: readonly (CpuRunState | null)[] = [
  null,
  "running",
  "waiting_for_node",
  "failed",
  "succeeded",
];

/**
 * The three cards, or the two that are true when nothing was measured.
 *
 * The terminal card names a size, and the size is the smallest count the
 * **server** published — a derivation, never a rule. Where the cluster-wide
 * read was refused there is no published list to derive it from, so the card
 * is absent rather than offering a number the console could not verify. That
 * is the same discipline every other figure on this screen follows.
 *
 * Args:
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     The actions to offer, in the order they are read.
 */
function cpuActions(cpu: CpuCapacity): CpuActionChoice[] {
  const cores = launcherCores(cpu);
  if (cores === null) {
    return [...CPU_ACTIONS];
  }
  return [
    ...CPU_ACTIONS,
    { action: "launcher", label: LAUNCHER_LABEL, note: launcherNote(cores) },
  ];
}

/** The smallest size this console offers, or null when it offers none. */
function launcherCores(cpu: CpuCapacity): number | null {
  const offered = cpu.defaults.allowed_workspace_cores;
  if (!cpu.measured || offered.length === 0) {
    return null;
  }
  return Math.min(...offered);
}

/**
 * CPU: cores with a shell, cores for a command, and the machines they come from.
 *
 * Two kinds live on this fleet and they are not variants of each other. A
 * **CPU workspace** is a machine you live on: cores, a deadline, an SSH door,
 * your `/home`. A **run** is a command: no shell, no port, no connection
 * block. Neither is a workspace with no GPU, and neither has an idle
 * countdown — nothing on a CPU machine separates computing from waiting, so
 * the console does not pretend it can tell.
 *
 * Because they are alternatives rather than a sequence, the screen shows one
 * of the two forms at a time, chosen by a pill — the same radio row the rest
 * of the console uses. Everything either form used to say is still here: the
 * sentences that are not needed to answer the question in front of you sit
 * behind a disclosure, never off the page.
 *
 * Returns:
 *     The CPU screen.
 */
export function CpuPage(): JSX.Element {
  const capacity = useCapacity();
  const environments = useEnvironments();
  const { me } = useSession();

  const cpu = capacity.data?.cpu ?? null;
  const [runState, setRunState] = useState<CpuRunState | null>(null);
  const runs = useCpuRuns(
    {
      mine: true,
      limit: RUN_LIST_LIMIT,
      ...(runState === null ? {} : { state: runState }),
    },
    cpu !== null,
  );
  const workspaces = useMyCpuWorkspaces(cpu !== null);
  const [action, setAction] = useState<CpuAction>("workspace");

  const header = (
    <PageHeader
      eyebrow="Cores"
      title="CPU"
      subtitle="Take a machine with cores and a shell for as long as you say, or give the console a command and how many times to run it — it finds the machines, runs every copy to the end and gives them back."
    />
  );

  if (isClusterProblem(capacity.error)) {
    return (
      <>
        {header}
        <ClusterProblem
          error={capacity.error}
          onRetry={() => void capacity.refetch()}
        />
      </>
    );
  }

  if (capacity.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the CPU machines…" />
      </>
    );
  }

  if (capacity.isError) {
    return (
      <>
        {header}
        <ErrorBlock
          error={capacity.error}
          onRetry={() => void capacity.refetch()}
        />
      </>
    );
  }

  if (cpu === null) {
    // `capacity.cpu` is null when the deployment has no CPU pools at all —
    // which is a different answer from "no cores are free", and it is said as
    // what it is. Which setting is missing belongs in the server's log and on
    // the admin screen, not in a researcher's face.
    return (
      <>
        {header}
        <p className="state-block">
          This console has no CPU machines, so it cannot run a command on
          cores. Nothing is broken and nothing of yours is affected — it is a
          setting on the console's side. Ask {SUPPORT_CHANNEL} if you need
          them.
        </p>
      </>
    );
  }

  const actions = cpuActions(cpu);
  // Capacity is polled, so the terminal card can go away under a researcher
  // who had chosen it — the read that publishes its size can fail at any
  // moment. The choice then falls back to the first card rather than leaving a
  // radio group with nothing selected.
  const chosen = actions.some((choice) => choice.action === action)
    ? action
    : "workspace";

  return (
    <>
      {header}

      {/* Workspaces first, commands second: the interactive thing is what
          somebody is waiting on, and it is the half of this fleet a
          researcher reaches for before they fan anything out. */}
      {workspaces.data && workspaces.data.length > 0 ? (
        <section className="section" aria-labelledby="cpu-workspace-list-title">
          <h2 className="section__title" id="cpu-workspace-list-title">
            Your CPU workspaces
          </h2>
          <ul className="job-list">
            {workspaces.data.map((workspace) => (
              <li key={workspace.id} className="job-row">
                <Link
                  className="job-row__name mono"
                  to={`/cpu-workspaces/${workspace.id}`}
                >
                  {workspace.image}
                </Link>
                <span className="badge" data-tone={workspaceTone(workspace.state)}>
                  <span className="badge__dot" aria-hidden="true" />
                  {workspaceStateLabel(workspace.state)}
                </span>
                <span className="job-row__meta">
                  {summarizeCpuWorkspaceRow(workspace)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The two primary actions of this fleet, side by side as a choice
          rather than stacked as two full forms: they are alternatives, and a
          researcher who wants one was reading past the other. Workspaces
          first, because the interactive thing is what somebody is waiting on.
          The pills are the console's own radio row — no new widget, and the
          keyboard walks them as it walks every other choice here. */}
      <section className="section" aria-labelledby="cpu-action-title">
        <h2 className="section__title" id="cpu-action-title">
          What do you need?
        </h2>
        <div
          className="choice-row"
          role="radiogroup"
          aria-labelledby="cpu-action-title"
        >
          {actions.map((choice) => (
            <label
              key={choice.action}
              className="choice"
              data-selected={chosen === choice.action}
            >
              <input
                type="radio"
                name="cpu-action"
                className="sr-only"
                value={choice.action}
                checked={chosen === choice.action}
                onChange={() => setAction(choice.action)}
              />
              {choice.label}
            </label>
          ))}
        </div>
        <p className="form__note">
          {actions.find((choice) => choice.action === chosen)?.note}
        </p>
        {environments.isPending ? (
          <LoadingBlock label="Reading the environments…" />
        ) : environments.isError ? (
          <ErrorBlock
            error={environments.error}
            onRetry={() => void environments.refetch()}
          />
        ) : chosen === "run" ? (
          <RunForm
            cpu={cpu}
            environments={environments.data.items}
            paused={me.console_paused}
            username={me.username}
          />
        ) : (
          // One form for both workspace cards, keyed on the card so switching
          // between them opens on that card's size instead of keeping the
          // other one's. A terminal is a CPU workspace and nothing new: the
          // same key gate, the same deadline, the same refusals.
          <CpuWorkspaceForm
            key={chosen}
            capacity={capacity.data}
            cpu={cpu}
            environments={environments.data.items}
            paused={me.console_paused}
            hasSshKey={hasKeyFor(me.ssh_public_keys, "workspaces")}
            slackNotifications={me.slack_notifications}
            preferredCores={chosen === "launcher" ? launcherCores(cpu) : null}
          />
        )}
      </section>

      {/* The CPU lane's own queue, counted in cores. The two fleets' lines are
          never merged, for the reason a CPU machine never appears among the
          GPU machines. */}
      <WaitingSection rows={cpu.queue ?? []} unit="core" you={me.username} />

      <RunPromises />

      <section className="section" aria-labelledby="run-list-title">
        <h2 className="section__title" id="run-list-title">
          Your runs
        </h2>
        <div
          className="choice-row"
          role="radiogroup"
          aria-label="Which runs to show"
        >
          {RUN_FILTERS.map((candidate) => (
            <label
              key={candidate ?? "all"}
              className="choice"
              data-selected={runState === candidate}
            >
              <input
                type="radio"
                name="run-filter"
                className="sr-only"
                value={candidate ?? "all"}
                checked={runState === candidate}
                onChange={() => setRunState(candidate)}
              />
              {candidate === null ? "All" : cpuRunStateLabel(candidate)}
            </label>
          ))}
        </div>
        {runs.isPending ? (
          <LoadingBlock label="Loading your runs…" />
        ) : runs.isError ? (
          <ErrorBlock error={runs.error} onRetry={() => void runs.refetch()} />
        ) : runs.data.items.length === 0 ? (
          <EmptyBlock
            label={
              runState === null
                ? "You have not submitted any run yet."
                : "None of your runs is in that state right now."
            }
          />
        ) : (
          <>
            <ul className="job-list">
              {runs.data.items.map((run) => (
                <li key={run.id} className="job-row">
                  <Link className="job-row__name mono" to={`/cpu/${run.id}`}>
                    {run.name}
                  </Link>
                  <span className="badge" data-tone={cpuRunTone(run)}>
                    <span className="badge__dot" aria-hidden="true" />
                    {cpuRunStateLabel(run.state)}
                  </span>
                  <span className="job-row__meta">{summarizeRunRow(run)}</span>
                </li>
              ))}
            </ul>
            {/* What was asked for and what came back, and not one word more:
                this endpoint publishes no total, so "50 of 312" would be a
                number invented on this side of the wire. */}
            {runs.data.items.length >= RUN_LIST_LIMIT ? (
              <p className="capacity__note">
                Showing the {RUN_LIST_LIMIT} most recent.
              </p>
            ) : null}
          </>
        )}
      </section>

      <CpuCapacitySection cpu={cpu} />
    </>
  );
}
